"use client"

import { useState, useEffect } from "react"
import { useWallet } from "@solana/wallet-adapter-react"
import { Connection, PublicKey } from "@solana/web3.js"
import { Loader2, RefreshCw } from "lucide-react"

interface WalletBalanceProps {
  connection: Connection
  irmaMint?: string
}

export function WalletBalance({ connection, irmaMint }: WalletBalanceProps) {
  const { publicKey } = useWallet()
  const [solBalance, setSolBalance] = useState<number>(0)
  const [irmaBalance, setIrmaBalance] = useState<number>(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (publicKey && connection) {
      loadBalances()
    } else {
      setSolBalance(0)
      setIrmaBalance(0)
      setLoading(false)
    }
  }, [publicKey, connection, irmaMint])

  const loadBalances = async () => {
    if (!publicKey) { setLoading(false); return }

    try {
      setLoading(true)
      const balance = await connection.getBalance(publicKey)
      setSolBalance(balance / 1e9)

      if (irmaMint && irmaMint !== "11111111111111111111111111111111") {
        try {
          const { getAssociatedTokenAddress, getAccount } = await import("@solana/spl-token")
          const mint = new PublicKey(irmaMint)
          const ata = await getAssociatedTokenAddress(mint, publicKey)
          const account = await getAccount(connection, ata)
          setIrmaBalance(Number(account.amount) / 1e9)
        } catch (err) {
          setIrmaBalance(0)
        }
      } else {
        setIrmaBalance(0)
      }
    } catch (err) {
      console.error("Error loading balances:", err)
    } finally {
      setLoading(false)
    }
  }

  if (!publicKey) return null

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
        <div className="px-1 mb-3 flex justify-between items-center">
          <div className="text-lg font-bold text-white">Wallet Balance</div>
          <button
            onClick={loadBalances}
            disabled={loading || !publicKey}
            className="text-[#8B949E] hover:text-white transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading ? (
          <div className="bg-[#1C1F26] rounded-2xl p-8 flex items-center justify-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-[#25e4e8]" />
            <span className="text-sm text-[#8B949E]">Loading balances...</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
              <p className="text-sm text-[#8B949E] mb-1">SOL Balance</p>
              <p className="text-[28px] font-bold text-white">{solBalance.toFixed(4)} SOL</p>
            </div>
            <div className="bg-[#1C1F26] rounded-2xl p-4 border border-[#25e4e8]/20">
              <p className="text-sm text-[#8B949E] mb-1">IRMA Balance</p>
              <p className="text-[28px] font-bold text-[#25e4e8]">{irmaBalance.toFixed(4)} IRMA</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
